/**
 * Products list page controller — /products（架構 A：分類瀏覽 storefront）
 *
 * 用戶定案 2026-06-04，取代舊「分類過濾 + 分頁」模型：
 *   1. 分類 chips = 錨點導覽：點 [data-scroll-chip] 平滑捲到對應分類 section（reduced-motion 直跳），
 *      不再過濾隱藏卡片。「全部」回頁首工具列。a[href="#id"] 保留無 JS 時的原生跳轉。
 *   2. 每分類一條首頁式左右輪播（捲動 / chevron 由 carousel.js 負責，本檔不碰）。
 *   3. 搜尋框切視圖：有關鍵字 → 隱藏 4 條分類輪播 section、顯示跨類聚合 .products-search-results
 *      grid（命中卡由各分類 li clone 進結果 grid）；清空 → 還原輪播、隱藏結果。
 *      命中判定沿用各卡 data-search（含產品名 + 品牌 + 區域 + 描述關鍵字）。
 *
 * clone 而非搬移：原卡留在輪播 section（清空即還原，無需搬回），結果 grid 放 clone。
 *   compare-bar 以 id 字串為鍵 + 全域委派 [data-add-compare]，且 syncSelected 用
 *   querySelectorAll('[data-product-id]') 同步「原卡 + clone」兩者 → 重複 data-product-id 為
 *   合法 data 屬性、不會重複加入，clone 的「加入比較」照常運作。
 *
 * 設計：root 守門早退，找不到工具列或分類段就 return，非該頁不執行、不報錯。
 */

// 共用 util；別名避免與本檔讀一次的布林區域變數 prefersReducedMotion 撞名（語義仍是讀一次）
import { prefersReducedMotion as prefersReducedMotionMQ } from '../utils/dom.js';

const SELECTORS = {
  chips: '.products-filter__chips',
  scrollChip: '[data-scroll-chip]',
  group: '.products-group',
  card: '.products-group__grid > li',
  search: '[data-list-search]',
  empty: '[data-list-empty]',
  results: '[data-search-results]',
  resultsGrid: '[data-search-results-grid]',
  clear: '[data-list-clear]', // 7.1 空態「清除搜尋」鈕（比照 admin/doctors）
};

function init() {
  const chipsRoot = document.querySelector(SELECTORS.chips);
  const groups = Array.from(document.querySelectorAll(SELECTORS.group));
  if (!chipsRoot || !groups.length) return null;

  const searchInput = document.querySelector(SELECTORS.search);
  const emptyEl = document.querySelector(SELECTORS.empty);
  const resultsEl = document.querySelector(SELECTORS.results);
  const resultsGrid = document.querySelector(SELECTORS.resultsGrid);

  const prefersReducedMotion = prefersReducedMotionMQ();

  /* ===== 1. 分類 chips 錨點平滑捲動 ===== */
  function onChipClick(event) {
    const chip = event.target.closest(SELECTORS.scrollChip);
    if (!chip || !chipsRoot.contains(chip)) return;

    const targetId = (chip.getAttribute('href') || '').replace(/^#/, '');
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return; // 找不到錨點 → 交回瀏覽器原生行為

    event.preventDefault();
    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }
  chipsRoot.addEventListener('click', onChipClick);

  /* ===== 2. 搜尋切視圖（輪播 ⇄ 聚合結果 grid） ===== */
  // 預先快取每張卡的可搜尋文字，避免每次 keystroke 重讀 DOM
  const cards = Array.from(document.querySelectorAll(SELECTORS.card));
  const cardMeta = cards.map((card) => ({
    card,
    search: (card.getAttribute('data-search') || '').toLowerCase(),
  }));

  /** 還原「分類瀏覽」視圖：顯示輪播 section、隱藏結果與空狀態。 */
  function showBrowseView() {
    groups.forEach((group) => { group.hidden = false; });
    if (resultsEl) resultsEl.hidden = true;
    if (resultsGrid) resultsGrid.replaceChildren();
    if (emptyEl) emptyEl.hidden = true;
  }

  /** 切到「搜尋結果」視圖：隱藏輪播 section、聚合命中卡 clone 進結果 grid。 */
  function showSearchView(keyword) {
    if (!resultsEl || !resultsGrid) return;

    const hits = cardMeta.filter(({ search }) => search.includes(keyword));

    // 批次組裝：DocumentFragment 一次掛載，避免 loop 內逐次 reflow
    const frag = document.createDocumentFragment();
    hits.forEach(({ card }) => frag.appendChild(card.cloneNode(true)));
    resultsGrid.replaceChildren(frag);

    // 命中卡可能來自從未進視窗的 below-fold 輪播卡（其 lazy 圖無 .is-loaded）。cloneNode 把
    // 「未載入態」原樣複製進結果 grid → 卡 main.css 的 img[loading=lazy]{opacity:0} 隱形。
    // 重綁讓 clone 出的圖套用 .is-loaded（已載入/cache 命中者立即標記，不等不會再 fire 的 load）。
    // 守 typeof 與 render-cards.js 同慣例：hook 未掛時靜默跳過。clone 完掛進 DOM 後才呼叫。
    if (typeof window !== 'undefined' && typeof window.__reapplyLazyReveal === 'function') {
      window.__reapplyLazyReveal(resultsGrid);
    }

    groups.forEach((group) => { group.hidden = true; });
    resultsEl.hidden = false;
    if (emptyEl) emptyEl.hidden = hits.length !== 0;
  }

  function onSearchInput() {
    const keyword = searchInput.value.trim().toLowerCase();
    if (keyword === '') {
      showBrowseView();
    } else {
      showSearchView(keyword);
    }
  }
  if (searchInput) searchInput.addEventListener('input', onSearchInput);

  /* ===== 7.1 空態「清除搜尋」：清空搜尋框 + 還原分類瀏覽視圖 =====
   * 本頁無「分類過濾 chip」（chips 為錨點導覽），故重置 = 清空搜尋框後派 input 事件，
   * 走既有 onSearchInput → keyword 空 → showBrowseView（還原輪播、隱藏結果與空態）。 */
  const clearBtn = document.querySelector(SELECTORS.clear);
  let onClear = null;
  if (clearBtn && searchInput) {
    onClear = () => {
      searchInput.value = '';
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      searchInput.focus();
    };
    clearBtn.addEventListener('click', onClear);
  }

  /* ===== cleanup（pagehide / bfcache） ===== */
  function destroy() {
    chipsRoot.removeEventListener('click', onChipClick);
    if (searchInput) searchInput.removeEventListener('input', onSearchInput);
    if (clearBtn && onClear) clearBtn.removeEventListener('click', onClear);
  }
  window.addEventListener('pagehide', destroy, { once: true });

  return { destroy };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
